/**
 * PushRevokedBanner — soft nudge shown on the dashboard when at least one
 * device in the family lost its push subscription (410/404 from the push
 * server) and THIS device also isn't currently subscribed.
 *
 * Auto-dismisses once the user either re-enables on this device, or snoozes
 * it for the day (sessionStorage — next reload after midnight shows again).
 * We never pop this on kiosk/kitchen-display devices because the point is
 * to nudge a phone owner, not to clutter the shared iPad.
 */

import React, { useEffect, useState } from "react";
import { BellOff, X } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported,
  pushSubscribedHere,
  enablePush,
} from "@/lib/push";
import { isKioskDevice } from "@/lib/kioskMode";
import { useI18n } from "@/lib/i18n";

const SNOOZE_KEY = "ff_push_revoked_snooze";

function snoozedToday() {
  try {
    const snoozed = sessionStorage.getItem(SNOOZE_KEY);
    return !!snoozed;
  } catch {
    return false;
  }
}

export default function PushRevokedBanner({ settings, viewerId, onRefresh }) {
  const { t } = useI18n();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    const check = async () => {
      if (!settings?.push_last_revoked_at) return;
      if (isKioskDevice()) return;
      if (snoozedToday()) return;
      if (!pushSupported()) return;
      // Only nag on this device if push isn't already enabled here.
      let subscribedHere = false;
      try {
        subscribedHere = await pushSubscribedHere();
      } catch {
        /* service worker unreachable — stay quiet rather than nagging */
      }
      if (!alive) return;
      if (!subscribedHere) setShow(true);
    };
    check();
    return () => {
      alive = false;
    };
  }, [settings?.push_last_revoked_at]);

  if (!show) return null;

  const reEnable = async () => {
    setBusy(true);
    try {
      await enablePush(viewerId);
      toast.success("Notifications back on");
      setShow(false);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.message || "Couldn't re-enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const snooze = () => {
    try {
      sessionStorage.setItem(SNOOZE_KEY, "1");
    } catch {
      /* ignore */
    }
    setShow(false);
  };

  return (
    <div
      className="ff-card p-4 mb-4 flex items-start gap-3 border border-[color:var(--line-soft)] bg-[color:var(--paper-2)]"
      data-testid="push-revoked-banner"
    >
      <BellOff className="w-5 h-5 mt-0.5 shrink-0 text-[color:var(--ink-2)]" strokeWidth={2} />
      <div className="flex-1 min-w-0">
        <div className="font-display-italic text-xl leading-snug">
          {t({
            en: "Notifications stopped on one of your devices.",
            da: "Notifikationer er stoppet på en af dine enheder.",
          })}
        </div>
        <div className="text-[13px] text-[color:var(--ink-2)] leading-snug mt-1">
          {t({
            en: "The 07:15 morning greet and walk reminders can't reach you until you re-enable. Tap below on whichever device went quiet.",
            da: "Morgen-hilsen kl. 07.15 og gåture-påmindelser kan ikke nå dig, før du slår dem til igen. Tryk nedenfor på den enhed, der gik i stå.",
          })}
        </div>
        <div className="flex gap-2 flex-wrap mt-3">
          <button
            type="button"
            onClick={reEnable}
            disabled={busy}
            className="ff-btn ff-btn-primary"
            data-testid="push-revoked-reenable"
          >
            {busy
              ? t({ en: "Enabling…", da: "Aktiverer…" })
              : t({ en: "Re-enable on this device", da: "Slå til igen på denne enhed" })}
          </button>
          <button
            type="button"
            onClick={snooze}
            className="ff-btn"
            data-testid="push-revoked-snooze"
          >
            {t({ en: "Not now", da: "Ikke nu" })}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={snooze}
        className="text-[color:var(--ink-3)] hover:text-[color:var(--ink-2)] -mt-1 -mr-1 p-1 shrink-0"
        aria-label={t({ en: "Dismiss", da: "Luk" })}
        data-testid="push-revoked-dismiss"
      >
        <X className="w-4 h-4" strokeWidth={2} />
      </button>
    </div>
  );
}
